import { Board } from "./Board";
import { useGame } from "../context/game-context";
import './Game.css';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { allGames } from "../model/games-database";

export enum GamePlayState {
    START, PLAY, WIN, LOSE
}

export interface GameState {
    playState: GamePlayState;
    ruleset: string;
}

export interface GameProperties {
    gameState: GameState;
    onStartGame: () => void;
    onChooseRuleset: (ruleset: string) => void;
}

export function Game({gameState, onStartGame, onChooseRuleset}: GameProperties) {
    const board = useGame();

    function getTitleText() {
        switch (gameState.playState) {
            case GamePlayState.WIN:
                return <h2>You Win!</h2>;
            case GamePlayState.LOSE:
                return <h2>You Lose!</h2>;
            default:
                return <h2>Play Solitaire</h2>;

        }
    }

    function handleRulesChange(e : SelectChangeEvent<string>) {
        onChooseRuleset(e.target.value);
    }

    return <div className="board-wrapper"><Board {...board} />
        {(gameState.playState !== GamePlayState.PLAY) ?  (
            <div className="board-overlay"> 
                <div className="overlay-container">
                    {getTitleText()}
                    <FormControl>
                        <InputLabel id="demo-simple-select-label">Choose Game</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={gameState.ruleset}
                            label="Choose Game"
                            onChange={handleRulesChange}
                            sx={{fontSize: '24px'}}
                        >
                            {Object.keys(allGames).map((rule) => <MenuItem value={rule}>{rule}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button variant="contained" onClick={onStartGame} sx={{fontSize: '24px', marginTop: '1em'}}>Start Game</Button>
                    <div className="description">
                        <p>🚀 Welcome to Fifty Solitaires: Your Ultimate Solitaire Destination! 🚀</p>
                        <p>Designed, planned and implemented by Holger Schmitz</p>
                        <p>Unleash your inner strategist with Fifty Solitaires, the ultimate online hub for the world's most 
                            beloved card game! Built with cutting-edge React technology, our platform brings together an incredible 
                            collection of solitaire variants designed to delight, challenge, and captivate players of all ages and skill levels.</p>
                        <p>🌟 Discover Three Thrilling Games Today - With More to Come! 🌟</p>
                        <p>Although I'm just getting started with three mind-blowing solitaire games, the innovative abstract model 
                            allows for seamless addition of new rule-sets. That means you'll soon be able to enjoy up to fifty different 
                            solitaire games right at your fingertips! Get ready for endless hours of entertainment as I continually expand 
                            the roster to bring you the most diverse and engaging solitaire experience on the web.</p>
                        <p>🎯 Exciting Next Steps on the Fifty Solitaires Roadmap 🎯</p>
                        <p>I'm committed to making Fifty Solitaires the most engaging and versatile solitaire platform available. 
                            Here's a sneak peek at the thrilling developments in store for our players:</p>
                        <ul>
                            <li>📱 Touch Device Support: We're working hard to expand our reach by enabling touch device compatibility. 
                                Soon, you'll be able to enjoy your favorite solitaire games on smartphones and tablets, bringing 
                                Fifty Solitaires with you wherever you go!</li>
                            <li>🔍 Automatic Detection of Possible Moves: To enhance gameplay and keep the momentum going, we're 
                                implementing a feature that automatically detects and suggests possible moves, ensuring you'll never 
                                miss an opportunity to advance in the game.</li>
                            <li>🎬 Smooth Card Movement Animations: Immerse yourself in a seamless gaming experience with our 
                                upcoming card movement animations, making every play feel more lifelike and satisfying.</li>
                            <li>💻 Responsive Layout: Our team is dedicated to optimizing the Fifty Solitaires interface for a wide 
                                range of devices and screen sizes. With a responsive layout, you'll enjoy a smooth and visually 
                                appealing gaming experience, no matter where you play.</li>
                            <li>🃏 More Games on the Horizon: As promised, we'll be expanding our collection to include at least 
                                fifty different solitaire games, offering an ever-growing library of challenges to master and enjoy.</li>
                        </ul>
                        <p>Stay tuned for these exciting updates, and don't forget to share your feedback and suggestions with our team. 
                            We're dedicated to continually improving and expanding Fifty Solitaires, and your input helps make that possible. 
                            Together, let's create the ultimate solitaire gaming destination!</p>
                    </div>
                </div>
            </div>) : undefined
        }
    </div>
}